<template lang="pug">
.approval-requests-page
  //- Page Header
  .page-header.mb-4
    h3.page-title
      Icon(icon="tabler:clipboard-check")
      | Session Swap & MC Requests
    div.d-flex(style="gap: 0.5rem")
      //- Guided walkthrough (auto-shows once; replay any time)
      b-button(variant="outline-dark" @click="$refs.tour.start()" title="Show me how this page works")
        Icon(icon="tabler:help-circle")
        | How to
      //- Approver tiers, limit, and availability
      b-button(variant="outline-secondary" @click="$bvModal.show('approver-settings-modal')" title="Approver Settings" data-tour="approvers")
        Icon(icon="tabler:settings")
        | Approvers
      //- Excel export of whichever list is open (MC requests or session swaps)
      b-button(variant="outline-success" @click="exportExcel" :disabled="exporting" :title="mainTab === 'mc' ? 'Export MC requests to Excel' : 'Export session swaps to Excel'" data-tour="export")
        b-spinner(small v-if="exporting")
        Icon(icon="tabler:file-spreadsheet" v-else)
        | {{ mainTab === 'mc' ? 'Export MC Excel' : 'Export Swaps Excel' }}
      //- Staff create a swap/MC on a client's behalf (families who can't use the app)
      b-button(variant="outline-primary" @click="$bvModal.show('staff-create-modal')" data-tour="new-request")
        Icon(icon="tabler:plus")
        | New Request
      b-button(variant="primary" @click="refreshData" :disabled="loading")
        b-spinner(small v-if="loading")
        Icon(icon="tabler:refresh" v-else)
        | Refresh

  how-to-tour(ref="tour" :steps="tourSteps" storage-key="howto_admin_requests_v1" auto-start)

  //- Request-type tabs: MC and Session Swaps are separate worlds — swaps are
  //- auto-approved (status 4) and would never show under an "Approved" filter.
  .main-tabs.mb-3(data-tour="main-tabs")
    button.main-tab(:class="{ active: mainTab === 'mc' }" @click="setMainTab('mc')")
      Icon(icon="tabler:medical-cross")
      span MC Requests
      span.count-pill.warning(v-if="mcCounts.pending") {{ mcCounts.pending }} pending
    button.main-tab(:class="{ active: mainTab === 'swap' }" @click="setMainTab('swap')")
      Icon(icon="tabler:repeat")
      span Session Swaps
      span.count-pill.info(v-if="swapCounts.upcoming") {{ swapCounts.upcoming }} upcoming

  //- Toolbar: status pills + search + dates + view toggle, one quiet row
  .toolbar.mb-3
    .sub-tabs(data-tour="status-tabs")
      button.sub-tab(
        v-for="tab in subTabs"
        :key="tab.key"
        :class="{ active: subTab === tab.key }"
        @click="setSubTab(tab.key)"
      )
        | {{ tab.label }}
        span.sub-count(v-if="tab.count") {{ tab.count }}
    .toolbar-filters(data-tour="filters")
      b-input-group.search-box(size="sm")
        b-input-group-prepend(is-text)
          Icon(icon="tabler:search")
        b-form-input(
          v-model="localFilters.searchQuery"
          placeholder="Search client..."
          @input="applyFilters"
        )
        b-input-group-append(v-if="localFilters.searchQuery")
          b-button(variant="outline-secondary" @click="clearSearch")
            Icon(icon="tabler:x")
      b-form-input.date-box(
        size="sm"
        type="date"
        v-model="localFilters.dateRange.start"
        @change="applyFilters"
        title="Submitted from"
      )
      b-form-input.date-box(
        size="sm"
        type="date"
        v-model="localFilters.dateRange.end"
        @change="applyFilters"
        title="Submitted until"
      )
      b-button(size="sm" variant="outline-secondary" @click="clearAllFilters" title="Clear filters")
        Icon(icon="tabler:filter-off")
      b-button-group(size="sm")
        b-button(
          :variant="viewMode === 'table' ? 'secondary' : 'outline-secondary'"
          @click="viewMode = 'table'"
          title="Table view"
        )
          Icon(icon="tabler:table")
        b-button(
          :variant="viewMode === 'card' ? 'secondary' : 'outline-secondary'"
          @click="viewMode = 'card'"
          title="Card view"
        )
          Icon(icon="tabler:layout-grid")

  //- Loading State
  .text-center.py-5(v-if="loading")
    b-spinner(variant="primary" label="Loading requests...")
    p.mt-3.text-muted Loading requests...

  //- Table View
  b-card.table-card(no-body v-else-if="viewMode === 'table' && visibleRequests.length > 0" data-tour="list")
    b-table.mb-0(
      :items="paginatedRequests"
      :fields="tableFields"
      hover
      responsive
      :busy="loading"
      show-empty
      tbody-tr-class="request-row"
      @row-clicked="viewDetails"
    )
      //- Client Name
      template(#cell(client)="data")
        div
          strong {{ data.item.crb5c_ClientLookup?.crb5c_no || 'N/A' }}
          br

      //- Request Type (MC tab only — distinguishes MC vs Other)
      template(#cell(crb5c_requesttype)="data")
        span.type-badge.mc
          | {{ data.value === 2 ? 'Other' : 'MC' }}

      //- Session Date(s) — swaps show the original date; MC requests show the
      //- covered date range (crb5c_originaldate is null for MC-range rows).
      template(#cell(crb5c_originaldate)="data")
        | {{ formatRequestDates(data.item) }}

      //- Replacement/Document
      template(#cell(replacement)="data")
        div(v-if="data.item.crb5c_requesttype === 0")
          | {{ formatDate(data.item.crb5c_replacementdate) }}
        div(v-else-if="data.item.crb5c_file")
          Icon.text-primary(icon="tabler:file-text")
          span  MC Submitted
        span.text-muted(v-else) No document

      //- Status
      template(#cell(crb5c_requeststatus)="data")
        span.status-badge(:class="statusDisplay(data.item).cls")
          | {{ statusDisplay(data.item).text }}

      //- Submitted On
      template(#cell(createdon)="data")
        | {{ formatDate(data.value) }}

      //- Actions. No quick Approve/Reject here on purpose: approvers must open
      //- the full request (reason, MC proof, billing outcome) and confirm from
      //- the detail modal. Clicking anywhere on the row opens it too.
      template(#cell(actions)="data")
        b-button(size="sm" variant="outline-primary" @click.stop="viewDetails(data.item)")
          Icon(icon="tabler:eye")
          | View Details

    //- Pagination
    b-pagination.my-3(
      v-model="currentPage"
      :total-rows="visibleRequests.length"
      :per-page="perPage"
      align="center"
    )

  //- Card View
  div(v-else-if="viewMode === 'card' && visibleRequests.length > 0" data-tour="list")
    b-row
      b-col.mb-3(
        md="6"
        lg="4"
        v-for="request in paginatedRequests"
        :key="request.crb5c_fowsessionmakeupid"
      )
        b-card.request-card.h-100(@click="viewDetails(request)")
          template(#header)
            .d-flex.justify-content-between.align-items-center
              span.type-badge(:class="request.crb5c_requesttype === 0 ? 'swap' : 'mc'")
                Icon(:icon="request.crb5c_requesttype === 0 ? 'tabler:repeat' : 'tabler:medical-cross'")
                | {{ request.crb5c_requesttype === 0 ? 'Swap' : request.crb5c_requesttype === 2 ? 'Other' : 'MC' }}
              span.status-badge(:class="statusDisplay(request).cls")
                | {{ statusDisplay(request).text }}

          .card-content
            h6.mb-2
              Icon(icon="tabler:user")
              | {{ request.crb5c_ClientLookup?.crb5c_no || 'N/A' }}
            
            .info-row
              Icon(icon="tabler:calendar")
              span
                strong {{ request.crb5c_requesttype === 0 ? 'Original: ' : 'MC Date(s): ' }}
                | {{ formatRequestDates(request) }}

            .info-row(v-if="request.crb5c_requesttype === 0")
              Icon(icon="tabler:calendar-check")
              span
                strong Replacement:
                | {{ formatDate(request.crb5c_replacementdate) }}

            .info-row(v-if="request.crb5c_requesttype === 1 && request.crb5c_file")
              Icon(icon="tabler:file-text")
              span MC Submitted

            .info-row
              Icon(icon="tabler:clock")
              span
                strong Submitted: 
                | {{ formatDate(request.createdon) }}

            .reason-preview.mt-2(v-if="request.crb5c_reason")
              small.text-muted {{ truncateText(request.crb5c_reason, 100) }}

          template(#footer)
            .card-actions
              b-button(size="sm" variant="outline-primary" @click.stop="viewDetails(request)")
                Icon(icon="tabler:eye")
                | View Details

    //- Pagination
    b-pagination.mt-3(
      v-model="currentPage"
      :total-rows="visibleRequests.length"
      :per-page="perPage"
      align="center"
    )

  //- Empty State
  .text-center.py-5(v-else)
    Icon.text-muted.mb-3(icon="tabler:clipboard-off" width="64" height="64")
    h5.text-muted No {{ mainTab === 'mc' ? 'MC requests' : 'session swaps' }} here
    p.text-muted Try another tab, adjust the filters, or check back later.

  //- Detail Modal
  ApprovalDetailModal(
    :request="selectedRequest"
    @approve="handleApproveFromModal"
    @reject="handleRejectFromModal"
  )

  //- Rejection Modal
  ApprovalRejectionModal(
    :request="selectedRequest"
    @reject="handleRejectionSubmit"
  )

  //- Staff manual creation (swap / MC on a client's behalf)
  StaffCreateRequestModal(@created="fetchRequests")

  //- Approver tiers, limit, and availability
  ApproverSettingsModal
</template>

<script>
import { Icon } from '@iconify/vue2';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { MAKEUP_REQUESTS_NAMESPACE } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import { ACTIONS, MUTATIONS } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import ApprovalDetailModal from '@/components/approval-requests/ApprovalDetailModal.vue';
import HowToTour from '@/components/HowToTour.vue';
import ApprovalRejectionModal from '@/components/approval-requests/ApprovalRejectionModal.vue';
import StaffCreateRequestModal from '@/components/approval-requests/StaffCreateRequestModal.vue';
import ApproverSettingsModal from '@/components/approval-requests/ApproverSettingsModal.vue';
import dayjs from 'dayjs';
import axios from 'axios';
import XLSX from 'xlsx-js-style';

export default {
  name: 'ApprovalRequestsPage',
  components: {
    Icon,
    ApprovalDetailModal,
    HowToTour,
    ApprovalRejectionModal,
    StaffCreateRequestModal,
    ApproverSettingsModal
  },
  data() {
    return {
      exporting: false,
      viewMode: 'table', // 'table' or 'card'
      // Guided walkthrough (HowToTour). Shown once per browser, replay via "How to".
      tourSteps: [
        { title: 'Session Swap & MC Requests', icon: 'tabler:clipboard-check', text: 'Everything caregivers submit from the app lands here: <b>MC / medical leave</b> requests that need your approval, and <b>session swaps</b>, which are applied automatically. Click <b>How to</b> any time to replay this guide.' },
        { title: '1 · Pick the request type', icon: 'tabler:switch-horizontal', target: 'main-tabs', text: '<b>MC Requests</b> wait for approval (the pill shows how many are pending). <b>Session Swaps</b> are approved automatically and applied by the nightly job — you only need to look here to cancel one.' },
        { title: '2 · Filter by status', icon: 'tabler:filter', target: 'status-tabs', text: 'Start with <b>Pending</b>. Approved and Rejected keep the history; <b>All</b> shows everything.' },
        { title: '3 · Search or narrow by date', icon: 'tabler:search', target: 'filters', text: 'Type a client name, or set a submitted-from / submitted-until date. The buttons on the right switch between table and card view.' },
        { title: '4 · Open a request', icon: 'tabler:eye', target: 'list', text: 'Click anywhere on a row (or <b>View Details</b>) to open it. The details show the reason, the MC photo, who submitted it, and the <b>billing outcome</b> — a credit note estimate when the dates are already invoiced.' },
        { title: '5 · Approve or reject', icon: 'tabler:check', text: 'Approve and Reject are inside the details only, so the full request is always seen first. Approvals above the tier-1 limit need a senior approver — the button is greyed out if it is not yours to approve. The caregiver gets a notification and email either way.' },
        { title: 'Approver Settings', icon: 'tabler:settings', target: 'approvers', text: 'Who approves what: tier-1 approvers up to the limit (default $200), senior approvers above it. Mark someone unavailable and requests route to the next person. Approval emails go to the people listed here.' },
        { title: 'Export', icon: 'tabler:file-spreadsheet', target: 'export', text: 'Downloads whichever list is open as Excel — MC requests with MC period, approver and billing status, or session swaps.' },
        { title: 'New Request', icon: 'tabler:plus', target: 'new-request', text: 'Submit an MC or swap on behalf of a family that cannot use the app. The MC proof is required unless you tick <b>Submit without proof</b> (when the physical MC is kept at the centre).' },
      ],
      currentPage: 1,
      perPage: 10,
      selectedRequest: null,
      // MC and swaps live in separate tabs — swaps are auto-approved (status 4)
      // and follow a different lifecycle than the approve/reject one.
      mainTab: 'mc', // 'mc' | 'swap'
      subTab: 'pending',
      localFilters: {
        requestType: null,
        searchQuery: '',
        dateRange: {
          start: null,
          end: null
        }
      }
    };
  },
  computed: {
    ...mapState(MAKEUP_REQUESTS_NAMESPACE, ['requests', 'filters', 'loading']),
    ...mapGetters(MAKEUP_REQUESTS_NAMESPACE, {
      filteredRequests: 'getFilteredRequests'
    }),

    // Search/date-filtered requests split by the active type tab.
    mcRequests() {
      return (this.filteredRequests || []).filter(r => r.crb5c_requesttype !== 0);
    },

    swapRequests() {
      return (this.filteredRequests || []).filter(r => r.crb5c_requesttype === 0);
    },

    mcCounts() {
      const list = this.mcRequests;
      return {
        pending: list.filter(r => r.crb5c_requeststatus === 0).length,
        approved: list.filter(r => r.crb5c_requeststatus === 1).length,
        rejected: list.filter(r => r.crb5c_requeststatus === 2).length,
        all: list.length
      };
    },

    swapCounts() {
      const list = this.swapRequests;
      return {
        upcoming: list.filter(r => r.crb5c_requeststatus === 4 && r.crb5c_swapapplied !== true).length,
        applied: list.filter(r => r.crb5c_swapapplied === true).length,
        cancelled: list.filter(r => r.crb5c_requeststatus === 2).length,
        all: list.length
      };
    },

    subTabs() {
      if (this.mainTab === 'mc') {
        return [
          { key: 'pending', label: 'Pending', count: this.mcCounts.pending },
          { key: 'approved', label: 'Approved', count: this.mcCounts.approved },
          { key: 'rejected', label: 'Rejected', count: this.mcCounts.rejected },
          { key: 'all', label: 'All', count: this.mcCounts.all }
        ];
      }
      return [
        { key: 'upcoming', label: 'Upcoming', count: this.swapCounts.upcoming },
        { key: 'applied', label: 'Applied', count: this.swapCounts.applied },
        { key: 'cancelled', label: 'Cancelled', count: this.swapCounts.cancelled },
        { key: 'all', label: 'All', count: this.swapCounts.all }
      ];
    },

    visibleRequests() {
      if (this.mainTab === 'mc') {
        const list = this.mcRequests;
        if (this.subTab === 'pending') return list.filter(r => r.crb5c_requeststatus === 0);
        if (this.subTab === 'approved') return list.filter(r => r.crb5c_requeststatus === 1);
        if (this.subTab === 'rejected') return list.filter(r => r.crb5c_requeststatus === 2);
        return list;
      }
      const list = this.swapRequests;
      if (this.subTab === 'upcoming') return list.filter(r => r.crb5c_requeststatus === 4 && r.crb5c_swapapplied !== true);
      if (this.subTab === 'applied') return list.filter(r => r.crb5c_swapapplied === true);
      if (this.subTab === 'cancelled') return list.filter(r => r.crb5c_requeststatus === 2);
      return list;
    },

    tableFields() {
      if (this.mainTab === 'mc') {
        return [
          { key: 'client', label: 'Name', sortable: false },
          { key: 'crb5c_requesttype', label: 'Type', sortable: true },
          { key: 'crb5c_originaldate', label: 'MC Dates', sortable: true },
          { key: 'replacement', label: 'Document', sortable: false },
          { key: 'crb5c_requeststatus', label: 'Status', sortable: true },
          { key: 'createdon', label: 'Submitted', sortable: true },
          { key: 'actions', label: 'Actions', sortable: false }
        ];
      }
      return [
        { key: 'client', label: 'Name', sortable: false },
        { key: 'crb5c_originaldate', label: 'Original Session', sortable: true },
        { key: 'replacement', label: 'Replacement', sortable: false },
        { key: 'crb5c_requeststatus', label: 'Status', sortable: true },
        { key: 'createdon', label: 'Submitted', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false }
      ];
    },

    paginatedRequests() {
      const filtered = this.visibleRequests;
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return filtered.slice(start, end);
    }
  },
  mounted() {
    this.fetchRequests();
  },
  methods: {
    // Excel export (xlsx-js-style) of the list currently open: the MC tab
    // exports MC requests, the Swaps tab exports session swaps. Rows come from
    // GET /api/makeup-requests/export. Header row styled, columns sized.
    async exportExcel() {
      this.exporting = true;
      try {
        const { data } = await axios.get('/api/makeup-requests/export');
        const isMc = this.mainTab === 'mc';
        const header = isMc
          ? ['Client', 'Schedule(s)', 'Type', 'Status', 'MC Period', 'No. of Dates', 'Processed in Billing', 'Applied On', 'Submitted By', 'Approved By', 'Approved On', 'Reason']
          : ['Client', 'From Session', 'Original Date', 'To Session', 'Replacement Date', 'Applied On', 'State', 'Reason'];
        const rows = isMc
          ? (data.mc || []).map(r => [r.client, r.schedules, r.type, r.status, r.mcDates, r.mcDateCount, r.billingProcessed, r.appliedOn, r.submittedBy, r.approvedBy, r.approvedOn, r.reason])
          : (data.swaps || []).map(r => [r.client, r.fromSession, r.originalDate, r.toSession, r.replacementDate, r.appliedOn, r.state, r.reason]);
        const widths = isMc
          ? [26, 34, 8, 10, 26, 8, 18, 17, 26, 26, 17, 40]
          : [26, 26, 13, 26, 16, 17, 24, 40];

        const headerStyle = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '50276B' } },
          alignment: { vertical: 'center', wrapText: true },
          border: { bottom: { style: 'thin', color: { rgb: '999999' } } }
        };
        const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
        header.forEach((_, c) => {
          const ref = XLSX.utils.encode_cell({ r: 0, c });
          if (ws[ref]) ws[ref].s = headerStyle;
        });
        ws['!cols'] = widths.map(wch => ({ wch }));
        ws['!freeze'] = { xSplit: 0, ySplit: 1 };
        ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(rows.length, 1), c: header.length - 1 } }) };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, isMc ? 'MC Requests' : 'Session Swaps');
        const stamp = dayjs().format('YYYY-MM-DD');
        XLSX.writeFile(wb, isMc ? `MC Requests ${stamp}.xlsx` : `Session Swaps ${stamp}.xlsx`);
        this.$bvToast.toast(`Exported ${rows.length} ${isMc ? 'MC request(s)' : 'session swap(s)'}`, { title: 'Export ready', variant: 'success', solid: true });
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to export', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.exporting = false;
      }
    },

    ...mapActions(MAKEUP_REQUESTS_NAMESPACE, {
      fetchRequests: ACTIONS.FETCH_REQUESTS,
      approveRequestAction: ACTIONS.APPROVE_REQUEST,
      rejectRequestAction: ACTIONS.REJECT_REQUEST,
      getCreditEstimateAction: ACTIONS.GET_CREDIT_ESTIMATE
    }),
    ...mapMutations(MAKEUP_REQUESTS_NAMESPACE, {
      SET_FILTERS: MUTATIONS.SET_FILTERS
    }),

    setMainTab(tab) {
      if (this.mainTab === tab) return;
      this.mainTab = tab;
      this.subTab = tab === 'mc' ? 'pending' : 'upcoming';
      this.currentPage = 1;
    },

    setSubTab(key) {
      this.subTab = key;
      this.currentPage = 1;
    },

    applyFilters() {
      this.SET_FILTERS(this.localFilters);
      this.currentPage = 1;
    },
    
    clearSearch() {
      this.localFilters.searchQuery = '';
      this.applyFilters();
    },
    
    clearAllFilters() {
      this.localFilters = {
        requestType: null,
        searchQuery: '',
        dateRange: {
          start: null,
          end: null
        }
      };
      this.applyFilters();
    },
    
    refreshData() {
      this.fetchRequests();
    },
    
    viewDetails(request) {
      this.selectedRequest = request;
      this.$bvModal.show('approval-detail-modal');
    },
    
    // Only pending (status 0) MC / Other requests can be approved. Swaps (type 0)
    // are auto-approved and applied by the swap processor — manually approving one
    // moves it off status 4 and the processor would never apply it (the backend
    // rejects it too; this just keeps the button hidden).
    canApproveRequest(request) {
      return !!request && request.crb5c_requesttype !== 0 && request.crb5c_requeststatus === 0;
    },

    // Pending MC / Other requests can be rejected; a submitted swap (status 4) can
    // be cancelled via reject while it hasn't been applied to attendance yet.
    canRejectRequest(request) {
      if (!request) return false;
      if (request.crb5c_requeststatus === 0) return true;
      return request.crb5c_requesttype === 0
        && request.crb5c_requeststatus === 4
        && request.crb5c_swapapplied !== true;
    },

    // Swaps show the original session date; MC/Other requests show the covered
    // date range from crb5c_mcdaterange (their crb5c_originaldate is null).
    formatRequestDates(request) {
      if (request.crb5c_requesttype !== 0 && request.crb5c_mcdaterange) {
        try {
          const raw = request.crb5c_mcdaterange;
          const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
          const dates = Array.isArray(obj?.dates) && obj.dates.length ? [...obj.dates].sort() : null;
          if (dates) {
            if (dates.length === 1) return this.formatDate(dates[0]);
            return `${this.formatDate(dates[0])} – ${this.formatDate(dates[dates.length - 1])} (${dates.length} days)`;
          }
          if (obj?.start && obj?.end) {
            return `${this.formatDate(obj.start)} – ${this.formatDate(obj.end)}`;
          }
        } catch (e) { /* fall through to originaldate */ }
      }
      return this.formatDate(request.crb5c_originaldate);
    },

    async approveRequest(request) {
      if (!this.canApproveRequest(request)) return;

      // Tell the approver up front whether this MC results in a credit note:
      // only dates already on an issued FOW invoice are credited; the rest get
      // no CN (they surface in a later CN report if invoiced later).
      let est = null;
      try {
        est = await this.getCreditEstimateAction(request.crb5c_fowsessionmakeupid);
      } catch (e) { /* estimate is informational — approval still guarded server-side */ }

      // Tiered policy says this admin can't approve this amount — say why up
      // front instead of walking them through a confirm dialog that will fail.
      if (est?.approval?.allowed === false) {
        this.$bvToast.toast(est.approval.reason || 'You are not authorized to approve this request', {
          title: 'Not Allowed',
          variant: 'warning',
          solid: true,
          noAutoHide: true
        });
        return;
      }

      // Structured dialog body (rows + colored indicator blocks) — a single
      // text paragraph here was unreadable.
      const h = this.$createElement;
      const infoRow = (label, value) =>
        h('div', { class: 'd-flex mb-1' }, [
          h('strong', { style: { minWidth: '90px', color: '#6b7280' } }, label),
          h('span', {}, value)
        ]);
      const block = (bg, color, icon, text) =>
        h('div', {
          class: 'p-2 mb-2',
          style: { background: bg, color, borderRadius: '8px', fontWeight: '600', fontSize: '0.88rem' }
        }, `${icon}  ${text}`);

      const body = [
        h('div', { class: 'mb-3' }, [
          infoRow('Client', request.crb5c_ClientLookup?.crb5c_no || 'this client'),
          infoRow('Type', request.crb5c_requesttype === 2 ? 'Other Request' : 'MC Request'),
          infoRow('MC Dates', this.formatRequestDates(request))
        ])
      ];

      if (est?.willReceiveCreditNote) {
        body.push(block(
          '#e8f5e9', '#1b5e20', '✓',
          `Credit note: estimated $${Number(est.estimatedCredit).toFixed(2)} for ${est.creditableDates.length} invoiced session(s)`
        ));
        if (est.notInvoicedDates?.length) {
          body.push(block(
            '#f1f2f6', '#4b5563', '—',
            `${est.notInvoicedDates.length} date(s) not on any FOW invoice yet — no credit note for these`
          ));
        }
      } else if (est) {
        body.push(block(
          '#f1f2f6', '#4b5563', '—',
          'No credit note — none of the MC dates are on an issued FOW invoice yet'
        ));
      }

      body.push(h('div', { class: 'small text-muted mt-3' },
        'Credit notes are issued manually by finance from the billing app export.'));

      const confirmed = await this.$bvModal.msgBoxConfirm(
        [h('div', {}, body)],
        {
          title: 'Confirm Approval',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'Yes, Approve',
          cancelTitle: 'Cancel',
          centered: true
        }
      );

      if (confirmed) {
        try {
          const result = await this.approveRequestAction(request.crb5c_fowsessionmakeupid);
          const estimated = result?.estimatedCredit;
          this.$bvToast.toast(
            estimated != null && estimated > 0
              ? `Request approved. Estimated credit for finance: $${Number(estimated).toFixed(2)}`
              : 'Request approved successfully',
            {
              title: 'Success',
              variant: 'success',
              solid: true
            }
          );
          await this.fetchRequests();
        } catch (error) {
          // Surface the server's reason — e.g. the tiered approval limit
          // ("above the $200 limit — must be approved by a senior approver").
          const serverMessage = error?.response?.data?.error;
          this.$bvToast.toast(serverMessage || 'Failed to approve request', {
            title: error?.response?.status === 403 ? 'Not Allowed' : 'Error',
            variant: 'danger',
            solid: true,
            noAutoHide: !!serverMessage
          });
        }
      }
    },
    
    rejectRequest(request) {
      this.selectedRequest = request;
      this.$bvModal.show('approval-rejection-modal');
    },
    
    handleApproveFromModal(request) {
      this.approveRequest(request);
    },
    
    handleRejectFromModal(request) {
      this.rejectRequest(request);
    },
    
    async handleRejectionSubmit({ request, reason }) {
      try {
        await this.rejectRequestAction({
          requestId: request.crb5c_fowsessionmakeupid,
          reason
        });
        this.$bvToast.toast('Request rejected successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        await this.fetchRequests();
        this.$bvModal.hide('approval-rejection-modal');
      } catch (error) {
        const serverMessage = error?.response?.data?.error;
        this.$bvToast.toast(serverMessage || 'Failed to reject request', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    
    // How many MC dates have been exported to finance (the billing app's CN
    // report writes them into the crb5c_mc_applied_dates ledger on export).
    exportedDatesCount(request) {
      try {
        const arr = JSON.parse(request.crb5c_mc_applied_dates || '[]');
        return Array.isArray(arr) ? arr.length : 0;
      } catch (e) {
        return 0;
      }
    },

    // Status label + badge class, aware of the swap lifecycle: a swap is
    // "Upcoming" until the processor applies it to attendance, then "Applied";
    // a rejected swap is a "Cancelled" one. Approved MCs additionally show
    // whether their CN application has been exported to finance.
    statusDisplay(request) {
      if (request.crb5c_requesttype === 0) {
        if (request.crb5c_swapapplied === true) return { text: 'Applied', cls: 'status-approved' };
        if (request.crb5c_requeststatus === 2) return { text: 'Cancelled', cls: 'status-rejected' };
        if (request.crb5c_requeststatus === 4) return { text: 'Upcoming', cls: 'status-submitted' };
      } else if (request.crb5c_requeststatus === 1) {
        // "Handled" = every MC date either went to finance on a CN listing or
        // was excluded from the invoice at generation time (no CN needed).
        if (request.crb5c_mc_billing_complete === true) {
          return { text: 'Billing Handled', cls: 'status-finance' };
        }
        if (this.exportedDatesCount(request) > 0) {
          return { text: 'Partly Handled', cls: 'status-finance' };
        }
        return { text: 'Approved', cls: 'status-approved' };
      }
      const map = {
        0: { text: 'Pending', cls: 'status-pending' },
        1: { text: 'Approved', cls: 'status-approved' },
        2: { text: 'Rejected', cls: 'status-rejected' },
        4: { text: 'Auto-Approved', cls: 'status-submitted' }
      };
      return map[request.crb5c_requeststatus] || { text: 'Unknown', cls: '' };
    },

    formatDate(date) {
      return date ? dayjs(date).format('DD MMM YYYY') : 'N/A';
    },
    
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    }
  }
};
</script>

<style scoped>
.approval-requests-page {
  padding: 1.5rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

/* --- Type tabs (MC vs Session Swaps): segmented control --- */
.main-tabs {
  display: flex;
  gap: 0.5rem;
}

.main-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.main-tab:hover {
  border-color: #cbd5e1;
  color: #374151;
}

.main-tab.active {
  background: #50276b;
  border-color: #50276b;
  color: #fff;
}

.count-pill {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
}

.count-pill.warning {
  background: #fff3cd;
  color: #856404;
}

.count-pill.info {
  background: #d1ecf1;
  color: #0c5460;
}

/* --- Toolbar: status pills + filters on one row --- */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.sub-tabs {
  display: flex;
  gap: 0.25rem;
  background: #f1f2f6;
  border-radius: 999px;
  padding: 0.25rem;
}

.sub-tab {
  border: none;
  background: transparent;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.sub-tab:hover {
  color: #374151;
}

.sub-tab.active {
  background: #fff;
  color: #50276b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.sub-count {
  margin-left: 0.35rem;
  font-size: 0.72rem;
  background: #e5e7eb;
  color: #4b5563;
  border-radius: 999px;
  padding: 0.05rem 0.4rem;
}

.sub-tab.active .sub-count {
  background: #efe6f7;
  color: #50276b;
}

.toolbar-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.search-box {
  width: 230px;
}

.date-box {
  width: 150px;
}

/* --- Table card --- */
.table-card {
  border: 1px solid #ececf1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: none;
}

.table-card ::v-deep thead th {
  background: #fafafc;
  border-top: none;
  border-bottom: 1px solid #ececf1;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.table-card ::v-deep td {
  vertical-align: middle;
  border-color: #f1f2f6;
}

.table-card ::v-deep tbody tr.request-row {
  cursor: pointer;
}

.request-card {
  cursor: pointer;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-weight: 500;
  font-size: 0.85rem;
}

.type-badge.swap {
  background-color: #e7f3ff;
  color: #0066cc;
}

.type-badge.mc {
  background-color: #ffe7e7;
  color: #cc0000;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.8rem;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.status-approved {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-rejected {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status-submitted {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

/* Approved MC whose CN application has been exported to finance */
.status-finance {
  background-color: #efe6f7;
  color: #50276b;
  border: 1px solid #ddc9ee;
}

.request-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.request-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card-content {
  padding: 0.5rem 0;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.reason-preview {
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.card-actions button {
  flex: 1;
}
</style>
